import { type ReactNode, useEffect, useState } from 'react';
import { Drawer } from '~/components/ui/drawer';
import { FilterActions } from '~/components/ui/card';

interface FilterDrawerProps<T extends object> {
  open: boolean;
  onClose: () => void;
  /** The applied filters. The draft restarts from them every time the drawer opens. */
  value: T;
  /** Receives the draft; the drawer closes itself after. */
  onApply: (draft: T) => void;
  /** Clears every filter; the drawer closes itself after. */
  onClear: () => void;
  testId?: string;
  /** The fields, bound to the draft. `patch` merges into it; nothing applies until Apply. */
  children: (draft: T, patch: (next: Partial<T>) => void) => ReactNode;
}

/** The one filter tray (DESIGN.md §10.2): titled "Filters", a draft that applies together, and a Clear all / Apply filters footer it owns. */
export function FilterDrawer<T extends object>({
  open,
  onClose,
  value,
  onApply,
  onClear,
  testId,
  children,
}: FilterDrawerProps<T>) {
  const [draft, setDraft] = useState(value);

  // Only opening restarts the draft: `value` is often a fresh object each render, which would wipe edits.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open]);

  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      title="Filters"
      width="md"
      testId={testId}
      footer={
        <FilterActions
          onClear={() => {
            onClear();
            onClose();
          }}
          onApply={() => {
            onApply(draft);
            onClose();
          }}
        />
      }
    >
      <div className="space-y-5">
        {children(draft, (next) => setDraft((current) => ({ ...current, ...next })))}
      </div>
    </Drawer>
  );
}
